import type { XrefPumps } from "@/composables/forms/formTypes";
import {
  getNominalPressureHeadFt,
  getNominalPowerBrakeHP
} from "@/libs/calculations/pumps";
import type {
  PeakHeadPoint,
  MaxHeadPoint,
  PeakBrakeHorsepowerPoint,
  StackBrakeHorsepowerPoint,
  StackHeadPoint
} from "./types";
import {
  getProducedBrakeHorsepowerForPumpAtFlowRateHP,
  getProducedHeadForPumpAtFlowRateFt
} from "./curves";

type PeakCurveScanPoint = {
  peakValue: number;
  peakFlowRateSTBD: number;
};

type PeakCurveSearchPass = {
  maxSteps: number;
  stepFraction: number;
  getStartFlowRateSTBD: (args: {
    maxGraphRate: number;
    bepRate: number;
    previousPeak?: PeakCurveScanPoint;
    previousPass?: PeakCurveSearchPass;
  }) => number;
};

const legacyHeadSearchPasses: PeakCurveSearchPass[] = [
  {
    maxSteps: 100,
    stepFraction: 0.01,
    getStartFlowRateSTBD: ({ maxGraphRate }) => maxGraphRate
  },
  {
    maxSteps: 230,
    stepFraction: 0.001,
    getStartFlowRateSTBD: ({ previousPeak, bepRate }) =>
      (previousPeak?.peakFlowRateSTBD || 0) + bepRate * 0.005
  },
  {
    maxSteps: 230,
    stepFraction: 0.00001,
    getStartFlowRateSTBD: ({ previousPeak, bepRate }) =>
      (previousPeak?.peakFlowRateSTBD || 0) + bepRate * 0.0005
  }
];

const workbookPeakSearchPasses: PeakCurveSearchPass[] = [
  {
    maxSteps: 100,
    stepFraction: 0.01,
    getStartFlowRateSTBD: ({ maxGraphRate }) => maxGraphRate
  },
  {
    maxSteps: 99,
    stepFraction: 0.0001,
    getStartFlowRateSTBD: ({ previousPeak, previousPass }) =>
      (previousPeak?.peakFlowRateSTBD || 0) *
      (1 + (previousPass?.stepFraction || 0) / 2)
  },
  {
    maxSteps: 99,
    stepFraction: 0.00001,
    getStartFlowRateSTBD: ({ previousPeak, previousPass }) =>
      (previousPeak?.peakFlowRateSTBD || 0) *
      (1 + (previousPass?.stepFraction || 0) / 2)
  }
];

const scanPeakPumpCurveValue = (
  evaluateCurve: (flowRateSTBD: number) => number,
  maxGraphRate: number,
  startFlowRateSTBD: number,
  stepFlowRateSTBD: number,
  maxSteps: number
): PeakCurveScanPoint => {
  let bestPoint = {
    peakValue: evaluateCurve(0),
    peakFlowRateSTBD: 0
  };

  for (let i = 0; i <= maxSteps; i++) {
    const flowRateSTBD = startFlowRateSTBD - i * stepFlowRateSTBD;
    if (flowRateSTBD < 0) {
      break;
    }
    if (flowRateSTBD > maxGraphRate) {
      continue;
    }

    const value = evaluateCurve(flowRateSTBD);

    if (value > bestPoint.peakValue) {
      bestPoint = {
        peakValue: value,
        peakFlowRateSTBD: flowRateSTBD
      };
    }
  }

  return bestPoint;
};

const getPeakPumpCurvePoint = (
  pump: NonNullable<XrefPumps[number]["r_pump"]>,
  evaluateCurve: (flowRateSTBD: number) => number,
  searchPasses: PeakCurveSearchPass[]
): PeakCurveScanPoint => {
  const maxGraphRate = pump.max_graph_rate_standard_pump_bpd || 0;
  const bepRate = pump.bep_rate_standard_pump_bpd || maxGraphRate;

  let previousPeak: PeakCurveScanPoint | undefined;
  let previousPass: PeakCurveSearchPass | undefined;

  for (const searchPass of searchPasses) {
    const startFlowRateSTBD = searchPass.getStartFlowRateSTBD({
      maxGraphRate,
      bepRate,
      previousPeak,
      previousPass
    });

    previousPeak = scanPeakPumpCurveValue(
      evaluateCurve,
      maxGraphRate,
      startFlowRateSTBD,
      startFlowRateSTBD * searchPass.stepFraction,
      searchPass.maxSteps
    );
    previousPass = searchPass;
  }

  return previousPeak || { peakValue: 0, peakFlowRateSTBD: 0 };
};

const getMaxCommonFlowRateBBLD = (
  xrefPumps: XrefPumps,
  frequencyRatio: number
): number => {
  return xrefPumps.reduce((minFlowRateBBLD, xrefPump) => {
    const pumpGraphMaxSTBD =
      xrefPump.r_pump?.max_graph_rate_standard_pump_bpd || 0;
    const rateCorrectionRatio = xrefPump.rate_correction_ratio || 1;
    const pumpGraphMaxBBLD =
      pumpGraphMaxSTBD * rateCorrectionRatio * frequencyRatio;

    return Math.min(minFlowRateBBLD, pumpGraphMaxBBLD);
  }, Number.POSITIVE_INFINITY);
};

const findCommonFlowPeakPoint = (
  maxCommonFlowRateBBLD: number,
  evaluateCurve: (flowRateBBLD: number) => number
): PeakCurveScanPoint => {
  let previousPeak: PeakCurveScanPoint | undefined;
  let previousPass: PeakCurveSearchPass | undefined;

  for (const searchPass of workbookPeakSearchPasses) {
    const startFlowRateBBLD = searchPass.getStartFlowRateSTBD({
      maxGraphRate: maxCommonFlowRateBBLD,
      bepRate: maxCommonFlowRateBBLD,
      previousPeak,
      previousPass
    });

    previousPeak = scanPeakPumpCurveValue(
      evaluateCurve,
      maxCommonFlowRateBBLD,
      startFlowRateBBLD,
      startFlowRateBBLD * searchPass.stepFraction,
      searchPass.maxSteps
    );
    previousPass = searchPass;
  }

  return previousPeak || { peakValue: 0, peakFlowRateSTBD: 0 };
};

export const getPeakHeadPointForPumpFt = (
  xrefPump: XrefPumps[number],
  gasHeadRerateRatio: number,
  frequencyRatio: number,
  pumpWearHeadCorrectionRatio: number
): PeakHeadPoint => {
  const pump = xrefPump.r_pump;
  if (!pump) {
    return { headFeet: 0, flowRateBBLD: 0, curveFlowRateSTBD: 0 };
  }

  const maxGraphRate = pump.max_graph_rate_standard_pump_bpd || 0;
  if (maxGraphRate <= 0) {
    return { headFeet: 0, flowRateBBLD: 0, curveFlowRateSTBD: 0 };
  }

  const peakPoint = getPeakPumpCurvePoint(
    pump,
    flowRateSTBD =>
      getNominalPressureHeadFt(
        flowRateSTBD,
        pump.hb,
        pump.h1,
        pump.h2,
        pump.h3,
        pump.h4,
        pump.h5
      ),
    legacyHeadSearchPasses
  );

  const correctedHeadPerStage =
    peakPoint.peakValue *
    xrefPump.head_correction_ratio *
    gasHeadRerateRatio *
    Math.pow(frequencyRatio, 2) *
    pumpWearHeadCorrectionRatio;

  const flowRateBBLD =
    peakPoint.peakFlowRateSTBD *
    (xrefPump.rate_correction_ratio || 1) *
    frequencyRatio;

  return {
    headFeet: correctedHeadPerStage * (xrefPump.total_stages || 1),
    flowRateBBLD,
    curveFlowRateSTBD: peakPoint.peakFlowRateSTBD
  };
};

export const getMaxHeadPointForPumpFt = (
  xrefPump: XrefPumps[number],
  gasHeadRerateRatio: number,
  frequencyRatio: number,
  pumpWearHeadCorrectionRatio: number
): MaxHeadPoint => {
  return getPeakHeadPointForPumpFt(
    xrefPump,
    gasHeadRerateRatio,
    frequencyRatio,
    pumpWearHeadCorrectionRatio
  );
};

export const getPeakBrakeHorsepowerPointForPump = (
  xrefPump: XrefPumps[number],
  frequencyRatio: number,
  fluidDensitySpecificGravity: number,
  pumpWearBHPCorrectionRatio: number
): PeakBrakeHorsepowerPoint => {
  const pump = xrefPump.r_pump;
  if (!pump) {
    return {
      brakeHorsepowerHP: 0,
      nominalBrakeHorsepowerHP: 0,
      flowRateBBLD: 0,
      curveFlowRateSTBD: 0
    };
  }

  const maxGraphRate = pump.max_graph_rate_standard_pump_bpd || 0;
  if (maxGraphRate <= 0) {
    return {
      brakeHorsepowerHP: 0,
      nominalBrakeHorsepowerHP: 0,
      flowRateBBLD: 0,
      curveFlowRateSTBD: 0
    };
  }

  const peakPoint = getPeakPumpCurvePoint(
    pump,
    flowRateSTBD =>
      Math.max(
        0,
        getNominalPowerBrakeHP(
          flowRateSTBD,
          pump.bhpb,
          pump.bhp1,
          pump.bhp2,
          pump.bhp3,
          pump.bhp4,
          pump.bhp5
        )
      ),
    workbookPeakSearchPasses
  );

  const correctedBrakeHorsepowerPerStageHP =
    peakPoint.peakValue *
    (xrefPump.brake_horsepower_correction_ratio || 1) *
    frequencyRatio ** 3 *
    fluidDensitySpecificGravity *
    pumpWearBHPCorrectionRatio;

  return {
    brakeHorsepowerHP:
      correctedBrakeHorsepowerPerStageHP * (xrefPump.total_stages || 1),
    nominalBrakeHorsepowerHP: peakPoint.peakValue,
    flowRateBBLD:
      peakPoint.peakFlowRateSTBD *
      (xrefPump.rate_correction_ratio || 1) *
      frequencyRatio,
    curveFlowRateSTBD: peakPoint.peakFlowRateSTBD
  };
};

export const findCommonFlowMaxBrakeHPPoint = (
  xrefPumps: XrefPumps,
  frequencyRatio: number,
  fluidDensitySpecificGravity: number,
  pumpWearBHPCorrectionRatio: number
): StackBrakeHorsepowerPoint => {
  if (
    !xrefPumps.length ||
    frequencyRatio <= 0 ||
    fluidDensitySpecificGravity <= 0
  ) {
    return {
      brakeHorsepowerHP: 0,
      flowRateBBLD: 0
    };
  }

  const maxCommonFlowRateBBLD = getMaxCommonFlowRateBBLD(
    xrefPumps,
    frequencyRatio
  );

  if (!Number.isFinite(maxCommonFlowRateBBLD) || maxCommonFlowRateBBLD <= 0) {
    return {
      brakeHorsepowerHP: 0,
      flowRateBBLD: 0
    };
  }

  const getTotalBrakeHPAtFlowRate = (flowRateBBLD: number) => {
    return xrefPumps.reduce((sum, xrefPump) => {
      return (
        sum +
        getProducedBrakeHorsepowerForPumpAtFlowRateHP(
          flowRateBBLD,
          xrefPump,
          frequencyRatio,
          fluidDensitySpecificGravity,
          pumpWearBHPCorrectionRatio
        )
      );
    }, 0);
  };

  const peakPoint = findCommonFlowPeakPoint(
    maxCommonFlowRateBBLD,
    getTotalBrakeHPAtFlowRate
  );

  return {
    brakeHorsepowerHP: peakPoint.peakValue || 0,
    flowRateBBLD: peakPoint.peakFlowRateSTBD || 0
  };
};

export const findCommonFlowMaxHeadPoint = (
  xrefPumps: XrefPumps,
  gasHeadRerateRatio: number,
  frequencyRatio: number,
  pumpWearHeadCorrectionRatio: number
): StackHeadPoint => {
  if (!xrefPumps.length || frequencyRatio <= 0) {
    return {
      headFeet: 0,
      flowRateBBLD: 0
    };
  }

  const maxCommonFlowRateBBLD = getMaxCommonFlowRateBBLD(
    xrefPumps,
    frequencyRatio
  );

  if (!Number.isFinite(maxCommonFlowRateBBLD) || maxCommonFlowRateBBLD <= 0) {
    return {
      headFeet: 0,
      flowRateBBLD: 0
    };
  }

  const getTotalHeadFeetAtFlowRate = (flowRateBBLD: number) => {
    return xrefPumps.reduce((sum, xrefPump) => {
      return (
        sum +
        getProducedHeadForPumpAtFlowRateFt(
          flowRateBBLD,
          xrefPump,
          gasHeadRerateRatio,
          frequencyRatio,
          pumpWearHeadCorrectionRatio
        )
      );
    }, 0);
  };

  const peakPoint = findCommonFlowPeakPoint(
    maxCommonFlowRateBBLD,
    getTotalHeadFeetAtFlowRate
  );

  return {
    headFeet: peakPoint.peakValue || 0,
    flowRateBBLD: peakPoint.peakFlowRateSTBD || 0
  };
};
